import React, { useEffect, useCallback, ReactNode } from 'react';
import 'twin.macro';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import SimpleModal from '@teetsh/app/src/components/SimpleModal';
import { WritableAtom } from 'jotai';

interface Labels {
  title: string;
  validateTitle?: string;
  cancelTitle?: string;
}

interface Props<T> {
  atom: WritableAtom<T | undefined, [T | undefined], void>;
  labels: Labels;
  onSave: () => void | Promise<void>;
  children: ReactNode;
}

export function EditTitleFieldsModal<T>({
  atom,
  labels,
  onSave,
  children,
}: Props<T>) {
  const { t } = useTranslation(['common']);
  const [payload, setPayload] = useAtom(atom);

  const onClose = () => {
    setPayload(undefined);
  };

  const handleSave = useCallback(async () => {
    if (!payload) return;

    await onSave();
    onClose();
  }, [payload, onSave]);

  useEffect(
    () => () => {
      setPayload(undefined);
    },
    [setPayload],
  );

  useEffect(() => {
    if (!payload) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [payload, handleSave]);

  if (!payload) return null;

  return (
    <SimpleModal
      isOpen={true}
      closeModalFct={onClose}
      title={labels.title}
      validateTitle={labels.validateTitle || t('common:buttons.save')}
      cancelTitle={labels.cancelTitle || t('common:buttons.cancel')}
      intent="primary"
      validateAction={handleSave}
    >
      <div tw="space-y-4">{children}</div>
    </SimpleModal>
  );
}
